import React, { useContext, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './Home.module.css';
import AuthContext, { StoredContext } from '../store/auth-context';
import Modal from './manage-user/Modal';
import Button, { ButtonType } from './general-components/Button';
import SuggestionSearch from './search/suggestion/SuggestionSearch';
import { FoldersOverview } from './group/FoldersOverview';
import GoToReportingButton from './reports/GoToReportingButton';
import ScanTimeDialog from './scan-time/ScanTimeDialog';
import { initialScanTimeEntries } from '../utils/scanTime';
import RIGHTS_KEYS from '../constants/rights-keys';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowUpRightFromSquare,
    faClockRotateLeft,
    faStopwatch,
} from '@fortawesome/free-solid-svg-icons';
import UnreadNotifications from './notifications/UnreadNotifications';
import OnboardingWizard from './onboarding/OnboardingWizard';

const Home = () => {
    const { t } = useTranslation();
    const [hasResult, setHasResult] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [semanticEnabled, setSemanticEnabled] = useState(false);
    const [scanTimeOpen, setScanTimeOpen] = useState(false);
    const [scanTimes, setScanTimes] = useState(initialScanTimeEntries);
    const matches = useMediaQuery('(min-width: 768px)');

    const context = useContext(AuthContext);
    let ctx: StoredContext = context.getContext();

    const [showTrialPrompt, setShowTrialPrompt] = useState(
        context.showTrialPrompt()
    );

    const closeTrialPrompt = () => {
        setShowTrialPrompt(false);
        context.setShowTrialPrompt(false);
    };

    const search = (term: string, s: boolean) => {
        setSearchTerm(term);
        setSemanticEnabled(s);
    };

    useEffect(() => {
        if (searchTerm.length > 0) {
            setHasResult(true);
        }
    }, [searchTerm]);

    return (
        <React.Fragment>
            {showTrialPrompt && (
                <Modal
                    info={{
                        title: '',
                        message: '',
                    }}
                    updateView={closeTrialPrompt}
                >
                    <div className={styles.trialBox}>
                        {ctx.actor.role === 'LiteUser' && (
                            <h1>{t('home.trialPrompt.expiredTitle')}</h1>
                        )}
                        <h4
                            style={{
                                marginBottom: '30px',
                            }}
                        >
                            {t('home.trialPrompt.upgradeMessage')}
                        </h4>
                        <Button
                            className={styles.trialBtn}
                            onClick={() => {
                                closeTrialPrompt();
                                window.location.href = '/user-profile';
                            }}
                            type={ButtonType.submit}
                        >
                            {t('home.trialPrompt.buttons.manageSubscription')}
                        </Button>
                    </div>
                </Modal>
            )}
            {!hasResult && (
                <div className={styles.wrapper}>
                    <div className={styles.topLeft}>
                        {context.orRights([RIGHTS_KEYS.cptReporting]) &&
                            matches && <GoToReportingButton />}
                        <Button
                            type={ButtonType.action}
                            onClick={() => setScanTimeOpen(true)}
                        >
                            <FontAwesomeIcon icon={faStopwatch} />
                            {t('scanTimeTracker.title')}
                        </Button>
                    </div>
                    <div className={styles.topRight}>
                        <UnreadNotifications />
                    </div>
                    <h2 className={styles.welcome}>
                        {t('home.welcome.title')}
                    </h2>
                    <div className={styles.search}>
                        {/* <Search
                            hasQuickResult={true}
                            onSearch={(term: string, s: boolean) =>
                                search(term, s)
                            }
                        /> */}
                        <SuggestionSearch
                            onSearch={(term: string, s: boolean) =>
                                search(term, s)
                            }
                        />
                    </div>
                    {/* <FrontpagePredefinedList /> */}
                    {context.orRights([RIGHTS_KEYS.manageAllImplants]) && (
                        <Button
                            type={ButtonType.tietary}
                            onClick={() => (window.location.href = '/all')}
                            dataOnboardingTarget="all-implants"
                        >
                            {t('home.buttons.seeAllImplants')}
                        </Button>
                    )}
                    {ctx.actor.role === 'PremiumUser' && (
                        <>
                            <Button
                                small
                                className={styles.historyButton}
                                type={ButtonType.primary}
                                onClick={() =>
                                    (window.location.href = '/history')
                                }
                                dataOnboardingTarget="history"
                            >
                                <FontAwesomeIcon icon={faClockRotateLeft} />
                                {t('home.buttons.history')}
                            </Button>
                            <div>
                                <h4 className={styles.actionsTitle}>
                                    {t('home.actions.title')}
                                </h4>
                                <div className={styles.actions}>
                                    <Button
                                        small
                                        type={ButtonType.action}
                                        onClick={() => {
                                            window.open(
                                                process.env
                                                    .REACT_APP_FEEDBACK_FORM,
                                                '_blank'
                                            );
                                        }}
                                    >
                                        {t('home.actions.feedback')}
                                        <FontAwesomeIcon
                                            icon={faArrowUpRightFromSquare}
                                        />
                                    </Button>
                                    <Button
                                        small
                                        type={ButtonType.action}
                                        onClick={() => {
                                            window.open(
                                                process.env
                                                    .REACT_APP_MISSING_FORM,
                                                '_blank'
                                            );
                                        }}
                                    >
                                        {t('home.actions.missingImplants')}
                                        <FontAwesomeIcon
                                            icon={faArrowUpRightFromSquare}
                                        />
                                    </Button>
                                </div>
                            </div>
                        </>
                    )}
                    <OnboardingWizard />
                    <FoldersOverview />
                </div>
            )}
            {scanTimeOpen && (
                <ScanTimeDialog
                    entries={scanTimes}
                    setEntries={setScanTimes}
                    onClose={() => setScanTimeOpen(false)}
                />
            )}
            {hasResult && (
                <Navigate
                    to={
                        '/search/query?term=' +
                        searchTerm +
                        (semanticEnabled
                            ? '&useSemantic=' + semanticEnabled
                            : '')
                    }
                />
            )}
        </React.Fragment>
    );
};

export default Home;
